import { useEffect, useMemo, useState } from "react";
import { Trash2, Plus, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import "@/components/teamproj/TeamProjections.css";

// Admin editor for per-team rotation-size bounds (how many players a team is allowed to use: min..max).
export function RotationBoundsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newTeam, setNewTeam] = useState("");
  const [mn, setMn] = useState(8);
  const [mx, setMx] = useState(12);

  useEffect(() => { api.get("/rotation-bounds").then((r) => setData(r.data)).catch((e) => toast.error(errorText(e))); }, []);

  const codeOf = useMemo(() => Object.fromEntries((data?.teams_list || []).map((t) => [t.id, t.code])), [data]);

  const add = async () => {
    if (!newTeam) return toast.error("Διάλεξε ομάδα");
    setBusy(true);
    try {
      const r = await api.put("/rotation-bounds", { team_id: newTeam, min: Number(mn), max: Number(mx) });
      setData({ ...data, teams: r.data.teams });
      setNewTeam("");
      toast.success(`${codeOf[newTeam] || newTeam}: ρότα ${mn}–${mx}`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };
  const remove = async (teamId) => {
    setBusy(true);
    try {
      const r = await api.delete(`/rotation-bounds/${teamId}`);
      setData({ ...data, teams: r.data.teams });
      toast.success(`${codeOf[teamId] || teamId}: επαναφορά στο προεπιλεγμένο`);
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  if (!data) return null;
  const entries = Object.entries(data.teams || {});
  const available = (data.teams_list || []).filter((t) => !(t.id in (data.teams || {})));
  const dmin = data.default?.min ?? 8, dmax = data.default?.max ?? 12;

  return (
    <div className="tp-root mt-6">
      <div className="tp-card" data-testid="rotation-bounds-card">
        <h4><Users size={13} className="inline mr-1" />ΟΡΙΑ ΡΟΤΑΣ ΑΝΑ ΟΜΑΔΑ <small>(admin · πόσους παίκτες χρησιμοποιεί μια ομάδα — προεπιλογή {dmin}–{dmax})</small></h4>
        <p className="tp-note" style={{ marginBottom: 6 }}>Το σύστημα κρατά τους κορυφαίους R παίκτες (R = μ.ο. παικτών ≥3' ανά ματς) εντός αυτών των ορίων· οι υπόλοιποι παίρνουν 0'.</p>

        {entries.length === 0 ? <p className="tp-note" data-testid="rotation-bounds-empty">Καμία ειδική ρύθμιση — όλες οι ομάδες στο {dmin}–{dmax}.</p> : (
          <div data-testid="rotation-bounds-list">
            {entries.map(([tid, b]) => (
              <div key={tid} className="tv-vform" data-testid={`rotation-bound-${tid}`} style={{ alignItems: "center" }}>
                <span style={{ fontWeight: 700, minWidth: 48 }}>{codeOf[tid] || tid}</span>
                <span className="tp-note">Ρότα {b.min}–{b.max} παίκτες</span>
                <button type="button" className="tv-vbtn" onClick={() => remove(tid)} disabled={busy} data-testid={`rotation-bound-remove-${tid}`}><Trash2 size={13} /> Επαναφορά</button>
              </div>
            ))}
          </div>
        )}

        <div className="tv-vform tv-margins" style={{ alignItems: "end", marginTop: 8 }}>
          <label><small>Ομάδα</small>
            <select value={newTeam} onChange={(e) => setNewTeam(e.target.value)} data-testid="rotation-bound-team">
              <option value="">— Διάλεξε ομάδα —</option>
              {available.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
            </select></label>
          <label><small>Min</small>
            <input type="number" min={data.hard?.min ?? 5} max={data.hard?.max ?? 15} value={mn} onChange={(e) => setMn(e.target.value)} data-testid="rotation-bound-min" /></label>
          <label><small>Max</small>
            <input type="number" min={data.hard?.min ?? 5} max={data.hard?.max ?? 15} value={mx} onChange={(e) => setMx(e.target.value)} data-testid="rotation-bound-max" /></label>
          <button type="button" className="tv-vbtn primary" onClick={add} disabled={busy} data-testid="rotation-bound-add"><Plus size={14} /> Προσθήκη</button>
        </div>
      </div>
    </div>
  );
}
